/**
 * 처음 설정(EA-03) — 새로.
 *
 * 결정: 사장님 「최초 최고관리자는 시스템에서 생성하고 거기서 직원을 생성하고 메뉴
 * 버튼별권한 지정하는걸로 하자」(기록: 직원은 첫 로그인 때 비밀번호·6자리 PIN 설정).
 * 최고관리자도 같다(거래소 01 6-32). 계약 XM-03.
 *
 * ⚠ **라우트가 아니다.** XM-06 의 `setupRequired` 가 true 면 주소와 상관없이 셸 대신
 * 이것을 그린다 — 첫 설정 전 세션은 XM-03·XM-05·XM-06 말고는 403 `setup_required` 다.
 *
 * ⚠ **두 번 입력한 것이 같은지는 화면이 본다.** 서버는 새 비밀번호·PIN 을 하나씩만
 * 받는다. 규칙(8~64자·영문·숫자·기호)은 서버가 판단한다(`invalid_password`).
 */
import { useState } from 'react'
import { firstSetup } from '@/api/admin'
import { ApiError, errorMessage } from '@/api/errors'
import { useSession } from '@/auth/session'
import { forgetWrite } from '@/auth/pendingWrite'
import { Button, Card, Field, Notice } from '@/components/ui'

const six = (v: string) => /^\d{6}$/.test(v)

export function FirstSetup() {
  const { refresh, signOut } = useSession()
  const [password, setPassword] = useState('')
  const [passwordAgain, setPasswordAgain] = useState('')
  const [pin, setPin] = useState('')
  const [pinAgain, setPinAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const passwordMismatch = password !== '' && passwordAgain !== '' && password !== passwordAgain
  const pinMismatch = pin !== '' && pinAgain !== '' && pin !== pinAgain
  const ready = password !== '' && passwordAgain !== '' && six(pin) && six(pinAgain)
    && !passwordMismatch && !pinMismatch

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!ready) return
    setBusy(true); setError(null)
    try {
      await firstSetup(password, pin)
      // ⚠ **입력한 값을 곧바로 버린다.** 화면 어디에도 오래 두지 않는다.
      setPassword(''); setPasswordAgain(''); setPin(''); setPinAgain('')
      // 이 세션은 PIN 을 통과한 것으로 본다(계약 XM-03) — XM-06 이 셸로 보낸다.
      await refresh()
    } catch (err) {
      setError(errorMessage(err))
      // 이미 끝난 설정 — 다른 창에서 끝냈을 수 있다. XM-06 이 다음 단계를 정한다.
      if (err instanceof ApiError && err.code === 'conflict') void refresh()
    } finally { setBusy(false) }
  }

  return (
    <div className="mx-auto mt-24 w-full max-w-sm">
      <h1 className="mb-6 text-center text-xl font-semibold">SJO Exchange Admin</h1>
      <Card title="First setup">
        {error && <div className="mb-4"><Notice tone="stop">{error}</Notice></div>}
        <form noValidate onSubmit={submit} className="flex flex-col gap-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            You signed in with a temporary password. Set a new password and a 6-digit PIN.
          </p>
          <Field label="New password" type="password" value={password} autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)} />
          <Field label="Confirm new password" type="password" value={passwordAgain} autoComplete="new-password"
            onChange={(e) => setPasswordAgain(e.target.value)} />
          {passwordMismatch && (
            <p className="text-xs text-red-700 dark:text-red-400">The new passwords do not match.</p>
          )}
          <p className="text-xs text-slate-500">8–64 characters, including letters, numbers and symbols.</p>
          <Field label="New PIN" type="password" inputMode="numeric" maxLength={6} value={pin}
            onChange={(e) => setPin(e.target.value)} />
          <Field label="Confirm new PIN" type="password" inputMode="numeric" maxLength={6} value={pinAgain}
            onChange={(e) => setPinAgain(e.target.value)} />
          {pinMismatch && <p className="text-xs text-red-700 dark:text-red-400">The new PINs do not match.</p>}
          <p className="text-xs text-slate-500">6 digits.</p>
          <Button type="submit" disabled={busy || !ready}>Set up</Button>
          <button type="button" onClick={() => { forgetWrite(); signOut() }}
            className="text-xs text-slate-500 underline">Sign in with another account</button>
        </form>
      </Card>
    </div>
  )
}
